"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsAssetGroupManagement = exports.googleAdsAssetGroupManagementArgsSchema = exports.GoogleAdsAssetGroupAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Asset Group ----------
var googleAdsCreateAssetGroupInputSchema = zod_1.z
    .object({
    campaignId: zod_1.z
        .string()
        .describe('The ID of the Performance Max campaign where the asset group will be created'),
    name: zod_1.z.string().min(1).max(255).describe('Name for the new asset group'),
    finalUrls: zod_1.z
        .array(zod_1.z.string().url())
        .min(1)
        .describe('Final URLs where users will land when clicking the ad'),
    headlines: zod_1.z
        .array(zod_1.z.string().max(30))
        .min(3)
        .max(5)
        .describe('Headlines for the asset group (3-5 required, max 30 chars each)'),
    longHeadlines: zod_1.z
        .array(zod_1.z.string().max(90))
        .min(1)
        .max(5)
        .describe('Long headlines for the asset group (1-5 required, max 90 chars each)'),
    descriptions: zod_1.z
        .array(zod_1.z.string().max(90))
        .min(2)
        .max(5)
        .describe('Descriptions for the asset group (2-5 required, max 90 chars each)'),
    businessName: zod_1.z.string().max(25).describe('Business name to display in ads (max 25 chars)'),
    marketingImages: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('URL of the marketing image'),
        name: zod_1.z.string().nullish().describe('Optional name for the image'),
    }))
        .min(1)
        .describe('REQUIRED: Marketing images for the asset group (at least 1 required). Recommended dimensions: Landscape 1200x628px (1.91:1), Square 1200x1200px (1:1), or Portrait 960x1200px (4:5). Min dimensions: Landscape 600x314px, Square 300x300px, Portrait 480x600px. Max file size 5MB. Use graphicsCreator to generate images at the recommended dimensions.'),
    squareMarketingImages: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('URL of the square marketing image'),
        name: zod_1.z.string().nullish().describe('Optional name for the image'),
    }))
        .min(1)
        .describe('REQUIRED: Square marketing images (1:1 aspect ratio, at least 1 required). Recommended: 1200x1200px, Min: 300x300px, Max file size: 5MB. Use graphicsCreator to generate images at the recommended dimensions.'),
    logoImages: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('URL of the logo image'),
        name: zod_1.z.string().nullish().describe('Optional name for the logo'),
    }))
        .min(1)
        .describe('REQUIRED: Logo images for the asset group (1:1 aspect ratio, at least 1 required). Recommended: 1200x1200px, Min: 128x128px, Max file size: 5MB. Use graphicsCreator to generate images at the recommended dimensions.'),
    landscapeLogoImages: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url().describe('URL of the landscape logo image'),
        name: zod_1.z.string().nullish().describe('Optional name for the logo'),
    }))
        .min(1)
        .nullish()
        .describe('Optional: Landscape logo images (4:1 aspect ratio). Recommended: 1200x300px, Min: 512x128px, Max file size: 5MB. Use graphicsCreator to generate images at the recommended dimensions.'),
    status: zod_1.z
        .enum(['ENABLED', 'PAUSED'])
        .default('ENABLED')
        .describe('Initial status for the asset group'),
})
    .describe('Create a new Google Ads asset group for Performance Max campaigns.');
var googleAdsCreateAssetGroupArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsCreateAssetGroupInputSchema);
var googleAdsCreateAssetGroupResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    assetGroupId: zod_1.z.string().nullish(),
    resourceName: zod_1.z.string().nullish(),
    message: zod_1.z.string().nullish(),
});
var googleAdsCreateAssetGroupResultSchema = googleAdsCreateAssetGroupResponseSchema;
// ---------- Update Asset Group ----------
var googleAdsUpdateAssetGroupInputSchema = zod_1.z
    .object({
    assetGroupId: zod_1.z.string().min(1).describe('The ID of the asset group to update'),
    name: zod_1.z.string().min(1).max(255).nullish().describe('New name for the asset group'),
    status: zod_1.z
        .enum(['ENABLED', 'PAUSED', 'REMOVED'])
        .nullish()
        .describe('New status for the asset group'),
    finalUrls: zod_1.z.array(zod_1.z.string().url()).nullish().describe('New final URLs for the asset group'),
})
    .describe('Update an existing Google Ads asset group.');
var googleAdsUpdateAssetGroupArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsUpdateAssetGroupInputSchema);
var googleAdsUpdateAssetGroupResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    resourceName: zod_1.z.string().nullish(),
    message: zod_1.z.string().nullish(),
});
var googleAdsUpdateAssetGroupResultSchema = googleAdsUpdateAssetGroupResponseSchema;
// ==========================
// Compound Tool: googleAdsAssetGroupManagement
// ==========================
exports.GoogleAdsAssetGroupAction = zod_1.z.enum(['createAssetGroup', 'updateAssetGroup']);
exports.googleAdsAssetGroupManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({
            name: zod_1.z.literal('createAssetGroup'),
            params: googleAdsCreateAssetGroupArgsSchema.shape.args,
        }),
        zod_1.z.object({
            name: zod_1.z.literal('updateAssetGroup'),
            params: googleAdsUpdateAssetGroupArgsSchema.shape.args,
        }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var GoogleAdsAssetGroupManagementAllResultsSchema = zod_1.z.union([
    googleAdsCreateAssetGroupResultSchema,
    googleAdsUpdateAssetGroupResultSchema,
]);
var googleAdsAssetGroupManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        {
            when: { 'action.name': 'createAssetGroup' },
            schema: googleAdsCreateAssetGroupResultSchema,
        },
        {
            when: { 'action.name': 'updateAssetGroup' },
            schema: googleAdsUpdateAssetGroupResultSchema,
        },
    ],
    defaultSchema: GoogleAdsAssetGroupManagementAllResultsSchema,
});
exports.googleAdsAssetGroupManagement = (0, common_1.createToolMetadata)({
    id: 'googleAdsAssetGroupManagement',
    displayName: 'Google Ads Asset Group Management',
    displayDescription: 'Create and update Google Ads asset groups for Performance Max campaigns',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsAssetGroupManagementArgsSchema,
}, googleAdsAssetGroupManagementResultSchemas);
